/**
 * Trip Planning API
 *
 * AI trip generation (async with job polling), item approval flow, regeneration.
 * Uses /trips/v1/* endpoints (JWT auth required).
 */

import type { AxiosInstance } from 'axios';
import type { Logger } from '@wg/api-client';
import type {
  AIGeneratedItem,
  AlternativeItem,
  ItemFeedbackInput,
  TripGenerationInput,
  TripGenerationStatus,
  TripGenerationSummary,
} from '@wg/types';

const TRIPS_BASE = '/trips/v1';

export interface GenerateTripResponse {
  request_id: string;
  status: TripGenerationStatus;
  poll_url: string;
}

export interface GenerationStatusResponse {
  status: TripGenerationStatus;
  progress?: number;
  itinerary_id?: string;
  error?: string;
  data?: TripGenerationSummary;
}

export interface GenerationJob {
  request_id: string;
  destination: string;
  status: TripGenerationStatus;
  created_at: string;
  itinerary_id?: string;
}

export interface RegenerateOptions {
  feedback?: string;
  keep_items?: string[];
  constraints?: string[];
}

export interface TripPlanningApi {
  generateTrip(input: TripGenerationInput): Promise<GenerateTripResponse>;
  getGenerationStatus(requestId: string): Promise<GenerationStatusResponse>;
  getGenerationJobs(): Promise<{ jobs: GenerationJob[] }>;
  regenerateTrip(
    requestId: string,
    options?: RegenerateOptions,
  ): Promise<{ request_id: string; status: TripGenerationStatus }>;
  approveItem(requestId: string, itemId: string): Promise<AIGeneratedItem>;
  rejectItem(
    requestId: string,
    itemId: string,
    feedback: ItemFeedbackInput,
  ): Promise<{ item: AIGeneratedItem; alternatives?: AlternativeItem[] }>;
  replaceItem(
    requestId: string,
    itemId: string,
    replacement: { provider_id: string; product_id: string },
  ): Promise<AIGeneratedItem>;
  approveAll(
    requestId: string,
    proceedToBooking?: boolean,
  ): Promise<{ approved_count: number; itinerary_id: string }>;
  getTripSummary(requestId: string): Promise<TripGenerationSummary>;
}

export function createTripPlanningApi(
  http: AxiosInstance,
  logger: Logger,
): TripPlanningApi {
  return {
    async generateTrip(input) {
      const url = `${TRIPS_BASE}/generate`;
      try {
        const response = await http.post<GenerateTripResponse>(url, input);
        logger.info('[TripPlanningAPI] Trip generation started', {
          requestId: response.data.request_id,
          destination: input.destination,
        });
        return response.data;
      } catch (error: any) {
        logger.error('[TripPlanningAPI] Failed to start trip generation', {
          error: error?.message,
          destination: input.destination,
        });
        throw error;
      }
    },

    async getGenerationStatus(requestId) {
      const startTime = Date.now();
      try {
        // Use shorter timeout for status polling - lightweight call, don't let
        // slow auth refresh block polling cadence
        const response = await http.get<GenerationStatusResponse>(
          `${TRIPS_BASE}/generate/${requestId}/status`,
          { timeout: 5000 },
        );
        const durationMs = Date.now() - startTime;
        logger.debug('[TripPlanningAPI] Generation status', {
          requestId,
          status: response.data.status,
          hasData: !!response.data.data,
          durationMs,
        });
        return response.data;
      } catch (error: any) {
        const durationMs = Date.now() - startTime;
        logger.warn('[TripPlanningAPI] Failed to get generation status', {
          requestId,
          error: error?.message,
          code: error?.code,
          status: error?.response?.status,
          isTimeout: error?.code === 'ECONNABORTED',
          durationMs,
        });
        throw error;
      }
    },

    async getGenerationJobs() {
      try {
        const response = await http.get<{ jobs: GenerationJob[] }>(
          `${TRIPS_BASE}/generate/jobs`,
        );
        return response.data;
      } catch (error: any) {
        logger.error('[TripPlanningAPI] Failed to get generation jobs', {
          error: error?.message,
        });
        throw error;
      }
    },

    async regenerateTrip(requestId, options) {
      try {
        const response = await http.post<{
          request_id: string;
          status: TripGenerationStatus;
        }>(`${TRIPS_BASE}/generate/${requestId}/regenerate`, options || {});
        logger.info('[TripPlanningAPI] Trip regeneration started', {
          requestId: response.data.request_id,
        });
        return response.data;
      } catch (error: any) {
        logger.error('[TripPlanningAPI] Failed to regenerate trip', {
          requestId,
          error: error?.message,
        });
        throw error;
      }
    },

    async approveItem(requestId, itemId) {
      try {
        const response = await http.post<AIGeneratedItem>(
          `${TRIPS_BASE}/${requestId}/items/${itemId}/approve`,
        );
        logger.info('[TripPlanningAPI] Item approved', { requestId, itemId });
        return response.data;
      } catch (error: any) {
        logger.error('[TripPlanningAPI] Failed to approve item', {
          requestId,
          itemId,
          error: error?.message,
        });
        throw error;
      }
    },

    async rejectItem(requestId, itemId, feedback) {
      try {
        const response = await http.post<{
          item: AIGeneratedItem;
          alternatives?: AlternativeItem[];
        }>(`${TRIPS_BASE}/${requestId}/items/${itemId}/reject`, feedback);
        logger.info('[TripPlanningAPI] Item rejected', {
          requestId,
          itemId,
          category: feedback.rejection_category,
        });
        return response.data;
      } catch (error: any) {
        logger.error('[TripPlanningAPI] Failed to reject item', {
          requestId,
          itemId,
          error: error?.message,
        });
        throw error;
      }
    },

    async replaceItem(requestId, itemId, replacement) {
      try {
        const response = await http.post<AIGeneratedItem>(
          `${TRIPS_BASE}/${requestId}/items/${itemId}/replace`,
          replacement,
        );
        logger.info('[TripPlanningAPI] Item replaced', { requestId, itemId });
        return response.data;
      } catch (error: any) {
        logger.error('[TripPlanningAPI] Failed to replace item', {
          requestId,
          itemId,
          error: error?.message,
        });
        throw error;
      }
    },

    async approveAll(requestId, proceedToBooking = false) {
      try {
        const response = await http.post<{
          approved_count: number;
          itinerary_id: string;
        }>(`${TRIPS_BASE}/${requestId}/approve-all`, {
          proceed_to_booking: proceedToBooking,
        });
        logger.info('[TripPlanningAPI] All items approved', {
          requestId,
          count: response.data.approved_count,
        });
        return response.data;
      } catch (error: any) {
        logger.error('[TripPlanningAPI] Failed to approve all items', {
          requestId,
          error: error?.message,
        });
        throw error;
      }
    },

    async getTripSummary(requestId) {
      try {
        const response = await http.get<TripGenerationSummary>(
          `${TRIPS_BASE}/${requestId}/summary`,
        );
        logger.debug('[TripPlanningAPI] Summary retrieved', {
          requestId,
          totalItems: response.data.total_items,
        });
        return response.data;
      } catch (error: any) {
        logger.error('[TripPlanningAPI] Failed to get summary', {
          requestId,
          error: error?.message,
        });
        throw error;
      }
    },
  };
}
